import { useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Clock } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { subscriptionService } from '@/services/api/subscription.service';
import { BILLING_STATUS_STALE_EVENT } from '@/lib/billing-events';
import { isNative } from '@/platform/native';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { BillingStatus } from '@/types/subscription';

const DAY = 24 * 60 * 60 * 1000;
const TRIAL_WARNING_DAYS = 3;
const BILLING_PATH = '/admin/billing';

const READ_ONLY_TEXT: Record<string, string> = {
  trial_ended: 'trialEnded',
  payment_lapsed: 'paymentLapsed',
  cancelled: 'cancelled',
};

function trialEndsSoon(status: BillingStatus): boolean {
  if (status.state !== 'trialing' || !status.trial_ends_at) return false;
  const left = new Date(status.trial_ends_at).getTime() - Date.now();
  return left > 0 && left <= TRIAL_WARNING_DAYS * DAY;
}

/**
 * The billing notice above every page of the app. A read-only account keeps its pages and data visible, so this
 * is the one place that says why writes are refused. Admins get a way to fix it; crew and staff are told to ask the owner.
 */
export function BillingBanner() {
  const { t, i18n } = useTranslation('subscription');
  const { user } = useAuth();
  const [location] = useLocation();
  const queryClient = useQueryClient();
  const enabled = user?.role === 'admin' || user?.role === 'staff' || user?.role === 'crew' || user?.role === 'estimator';

  const { data: status } = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: () => subscriptionService.getBillingStatus(),
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  // A write refused with subscription_required (see api-client) makes the cached status stale.
  useEffect(() => {
    const refetch = () => queryClient.invalidateQueries({ queryKey: ['billing', 'status'] });
    window.addEventListener(BILLING_STATUS_STALE_EVENT, refetch);
    return () => window.removeEventListener(BILLING_STATUS_STALE_EVENT, refetch);
  }, [queryClient]);

  const longDate = (value: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(new Date(value));

  if (!enabled || !status || location.startsWith(BILLING_PATH)) return null;

  const isAdmin = user?.role === 'admin';
  let message: string | null = null;
  let tone: 'danger' | 'soft' = 'danger';
  let showChoosePlan = false;
  let action: 'fixPayment' | null = null;

  if (status.state === 'read_only' && status.reason === 'account_on_hold') {
    // A platform hold: no plan fixes it, so there is no Choose a plan button, for owners or crew.
    message = t('billing.banner.onHold');
  } else if (status.state === 'read_only' && isNative()) {
    // The app shows the state and nothing to act on: no plan name, no button, no way to buy (store policy).
    message = t('billing.native.readOnly');
  } else if (status.state === 'read_only') {
    message = isAdmin
      ? t(`billing.banner.${READ_ONLY_TEXT[status.reason ?? 'cancelled'] ?? 'cancelled'}`)
      : t('billing.crewReadOnly');
    showChoosePlan = isAdmin;
  } else if (isAdmin && !isNative() && status.state === 'past_due') {
    // Inside the grace period nothing is locked yet; say so before it becomes read-only
    message = status.grace_ends_at
      ? t('billing.banner.pastDueBy', { date: longDate(status.grace_ends_at) })
      : t('billing.banner.pastDue');
    tone = 'soft';
    action = 'fixPayment';
  } else if (isAdmin && !isNative() && status.over_seat_limit) {
    message = t('billing.overSeatLimit');
  } else if (isAdmin && !isNative() && trialEndsSoon(status)) {
    message = t('billing.trialEndsOn', { date: longDate(status.trial_ends_at as string) });
    tone = 'soft';
    showChoosePlan = true;
  }

  if (!message) return null;

  const Icon = tone === 'soft' ? Clock : AlertCircle;

  return (
    <div
      role={tone === 'soft' ? 'status' : 'alert'}
      className={cn(
        'flex flex-col gap-2 rounded-lg border px-3 py-2 sm:flex-row sm:items-center sm:gap-3',
        tone === 'soft' ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-red-200 bg-red-50 text-red-900',
      )}
    >
      <Icon className="hidden h-4 w-4 flex-shrink-0 sm:block" aria-hidden="true" />
      <p className="min-w-0 flex-1 text-sm">{message}</p>
      {action === 'fixPayment' && (
        <Link href={BILLING_PATH}>
          <Button size="sm" variant="outline" className="whitespace-nowrap bg-white">
            {t('billing.fixPayment')}
          </Button>
        </Link>
      )}
      {showChoosePlan && (
        <Link href={BILLING_PATH}>
          <Button size="sm" variant="outline" className="whitespace-nowrap bg-white">
            {t('billing.choosePlan')}
          </Button>
        </Link>
      )}
    </div>
  );
}
